/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

import React, { useEffect } from "react";

import { useStateContext, setViewAction } from "../../context/StateContext";
import checkForceAuth from "../../hooks/checkForceAuth";
import handleAuthUIStateChange from "../../hooks/handleAuthStateChange";
import SignInContainer from "./signInContainer";
import AuthenticatedStatus from "../../hooks/isAuthenticated";
import getStringParams from "../../hooks/getStringParams";
import LoadingPage from "./loadingPage";
import { screenConditionCheck } from "./screenConditionCheck";
import { QUERY_PARAMS_KEYS } from "../../const";

const LandingPage = () => {
	const isAuthenticated = AuthenticatedStatus();
	const { state, dispatch } = useStateContext();

	const { authorization_code } = getStringParams();

	useEffect(() => {
		// Direct-access rescue: if the user lands at `/` without any
		// OAuth-flow parameter, the auth flow cannot proceed. Redirect
		// them to the license portal so they have somewhere to go.
		if (window.location.pathname !== "/") return;

		const searchParams = new URLSearchParams(window.location.search);
		const oauthParams = [
			"client_id",
			"redirect_uri",
			"code",
			"authorization_code",
			"code_challenge",
			"code_challenge_method",
			"state",
			"response_type",
			"error",
			"access_token",
			"id_token",
			"forceAuth",
			"scope",
		];
		const hasOAuthParam = oauthParams.some((p) => searchParams.has(p));

		if (!hasOAuthParam) {
			const REDIRECTS = {
				"https://s3t-auth-staging.studio3t.com": "https://license-cognito-staging.studio3t.com/",
				"https://s3t-auth.studio3t.com":         "https://license.studio3t.com/",
			};
			const target = REDIRECTS[window.location.origin];
			if (target) window.location.replace(target);
		}

	}, []);

	useEffect(() => {
		const googleRedirectCallback = () => {
			dispatch(setViewAction("GOOGLE_SIGN_UP"));
		};

		window.addEventListener("newGoogleUser", googleRedirectCallback);

		return () => {
			window.removeEventListener("newGoogleUser", googleRedirectCallback);
		};
	}, [dispatch]);

	useEffect(() => {
		if (isAuthenticated === null) return;
		checkForceAuth();
		handleAuthUIStateChange(isAuthenticated);
	}, [isAuthenticated]);

	useEffect(() => {
		const searchParams = new URLSearchParams(window.location.search);
		if (
			!searchParams.get(QUERY_PARAMS_KEYS.originURI) &&
			document.referrer
		) {
			searchParams.set(
				QUERY_PARAMS_KEYS.originURI,
				document.referrer.replace(/\/$/, "")
			);
			window.history.pushState(
				{},
				"",
				`${window.location.origin}/?${searchParams.toString()}`
			);
		}
	}, []);

	const isVisible = screenConditionCheck(
		state.view,
		authorization_code,
		isAuthenticated
	);

	if (isVisible) {
		console.log("Show sign in screen due to screenConditionCheck()");
		return <SignInContainer />;
	}

	console.log("Show loading screen due to screenConditionCheck()");
	console.log(
		JSON.stringify(
			{
				view: state.view,
				authorization_code,
				isAuthenticated,
			},
			null,
			2
		)
	);

	return <LoadingPage text="Loading" />;
};

export default LandingPage;
